import { Button, IconButton, useDisclosure } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import PlusIcon from 'src/assets/icons/Plus';
import Help from 'src/components/menu/Help';
import { PageHeaderContainer, PageHeading } from 'src/components/page/PageHeader';
import { usePermissionsContext } from 'src/components/permissions/PermissionsContext';
import useMobileLayout from 'src/hooks/useMobileLayout';
import colors from 'src/theme/colors';
import hexToRgb from 'src/utils/hexToRgb';
import { EpisodesCountPerUserDocument, useCreateEpisodeMutation } from 'src/generated/graphql';
import useCanCreateEpisode from 'src/pages/episodes/useCanCreateEpisode';
import EpisodesFilter from './filter/EpisodesFilter';
import UpgradeSubscriptionDialog from '../UpgradeSubscriptionDialog';

export default function TopSection() {
  return (
    <PageHeaderContainer
      templateColumns="1fr auto"
      templateAreas={`"heading" "subheading"`}
      gridRowGap="1px"
      headerProps={{ paddingBottom: '0px' }}
    >
      <PageHeading gridArea="heading" pb="24px">
        My Episodes <Help />
      </PageHeading>
      <AddEpisodeButton />
      <EpisodesFilter />
    </PageHeaderContainer>
  );
}

function AddEpisodeButton() {
  const canCreateEpisode = useCanCreateEpisode();
  const createEpisode = useCreateEpisode();
  const { open, onOpen, onClose } = useDisclosure();
  const isMobileLayout = useMobileLayout();

  // Workaround for permissions not being fetched for some legacy users
  const { permissions, refetchPermissions } = usePermissionsContext();
  useEffect(() => {
    if (!permissions) {
      refetchPermissions();
    }
  }, [permissions, refetchPermissions]);

  switch (isMobileLayout) {
    case true: {
      return (
        <IconButton h="36px" w="36px" aria-label="create episode" onClick={() => createEpisode()}>
          <PlusIcon fill={hexToRgb(colors.white, 0.8)} />
        </IconButton>
      );
    }
    case false: {
      return (
        <>
          <Button
            onClick={() => {
              if (!canCreateEpisode) {
                onOpen();
                return;
              }
              createEpisode();
            }}
            h="48px"
            w="197px"
            justifySelf="end"
          >
            <PlusIcon fill={colors.white} />
            New episode
          </Button>
          <UpgradeSubscriptionDialog open={open} onClose={onClose} />
        </>
      );
    }
  }
}

export function useCreateEpisode() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [createEpisodeFn] = useCreateEpisodeMutation({
    variables: { metaData: { name: `New Episode ${new Date().toLocaleString()}` } },
    onCompleted: ({ episode }) => {
      navigate(`/upload/${episode.id}`, {
        state: { status: 'noUploads', previousPathname: pathname },
      });
    },
    refetchQueries: [
      {
        query: EpisodesCountPerUserDocument,
      },
    ],
  });

  return createEpisodeFn;
}
